import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Input, Modal, Rate, Typography, message } from '@/shared/antd-imports';
import { useRateMilestoneMutation } from '@/store/api';
import type { ClientMilestone } from '@/types/milestone-ratings';
import { isValidRating, MAX_RATING_COMMENT } from './milestone-rating-utils';

interface RateDeliveryModalProps {
  projectId: string;
  milestone: ClientMilestone;
  onClose: () => void;
}

/**
 * Spec 15: the same question at every delivered milestone, so answers compare over time:
 * "How happy are you with this delivery?" 1–5 plus an optional comment. Answering again changes the rating.
 */
export const RateDeliveryModal: React.FC<RateDeliveryModalProps> = ({ projectId, milestone, onClose }) => {
  const { t } = useTranslation();
  const [rating, setRating] = useState<number>(milestone.my_rating?.rating ?? 0);
  const [comment, setComment] = useState<string>(milestone.my_rating?.comment ?? '');
  const [rate, { isLoading }] = useRateMilestoneMutation();
  const labels = [
    t('milestoneRatings.levels.1', { defaultValue: 'Very unhappy' }),
    t('milestoneRatings.levels.2', { defaultValue: 'Unhappy' }),
    t('milestoneRatings.levels.3', { defaultValue: 'Okay' }),
    t('milestoneRatings.levels.4', { defaultValue: 'Happy' }),
    t('milestoneRatings.levels.5', { defaultValue: 'Very happy' }),
  ];

  const onSubmit = async () => {
    if (!isValidRating(rating)) return;
    try {
      const result = await rate({ projectId, milestoneId: milestone.id, rating, comment: comment.trim() || undefined }).unwrap();
      if (result && result.done === false) throw new Error(result.message);
      message.success(t('milestoneRatings.thanks', { defaultValue: 'Thank you for your feedback' }));
      onClose();
    } catch {
      message.error(t('milestoneRatings.saveError', { defaultValue: 'Your rating could not be saved. Please try again.' }));
    }
  };

  return (
    <Modal
      open
      title={t('milestoneRatings.question', { defaultValue: 'How happy are you with this delivery?' })}
      okText={t('milestoneRatings.submit', { defaultValue: 'Send rating' })}
      cancelText={t('milestoneRatings.cancel', { defaultValue: 'Cancel' })}
      okButtonProps={{ disabled: !isValidRating(rating) }}
      confirmLoading={isLoading}
      onOk={() => void onSubmit()}
      onCancel={onClose}
      destroyOnClose
    >
      <Flex vertical gap={12}>
        <Typography.Text strong>{milestone.name}</Typography.Text>
        <Flex align="center" gap={12} wrap="wrap">
          <Rate value={rating} onChange={setRating} tooltips={labels} autoFocus />
          {isValidRating(rating) && <Typography.Text type="secondary">{labels[rating - 1]}</Typography.Text>}
        </Flex>
        <Input.TextArea
          value={comment}
          onChange={event => setComment(event.target.value)}
          maxLength={MAX_RATING_COMMENT}
          showCount
          autoSize={{ minRows: 3, maxRows: 8 }}
          placeholder={t('milestoneRatings.commentPlaceholder', {
            defaultValue: 'What went well, and what could be better? (optional)',
          })}
          aria-label={t('milestoneRatings.comment', { defaultValue: 'Comment' })}
        />
      </Flex>
    </Modal>
  );
};
